import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Eye, Check, X, ShieldCheck, Trash2, Save, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { Checkbox } from '@/Components/ui/Checkbox';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import { TableActions } from '@/Components/ui/TableActions';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import qualidadeInspecaoService, {
  type QualidadeInspecao,
  type QualidadeChecklistItem,
  type QualidadeTipo,
  type QualidadeResultado,
} from '@/services/qualidadeInspecao';
import loteService, { type Lote } from '@/services/lote';
import ordemProducaoService, { type OrdemProducao } from '@/services/ordemProducao';
import responsavelService, { type Responsavel } from '@/services/responsavel';

const TIPO_LABELS: Record<QualidadeTipo, string> = {
  ENTRADA: 'Entrada',
  PROCESSO: 'Processo',
  FINAL: 'Final',
};
const TIPO_TONES: Record<QualidadeTipo, Tone> = {
  ENTRADA: 'info',
  PROCESSO: 'warning',
  FINAL: 'purple',
};
const RESULTADO_LABELS: Record<QualidadeResultado, string> = {
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
  APROVADO_COM_RESSALVAS: 'Aprovado c/ Ressalvas',
};
const RESULTADO_TONES: Record<QualidadeResultado, Tone> = {
  APROVADO: 'success',
  REPROVADO: 'danger',
  APROVADO_COM_RESSALVAS: 'warning',
};

function formatDateTime(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('pt-BR');
}

const FORM_VAZIO = {
  tipo: 'ENTRADA' as QualidadeTipo,
  lote_id: '',
  op_id: '',
  data_inspecao: '',
  resultado: 'APROVADO' as QualidadeResultado,
  responsavel: '',
  acao_corretiva: '',
  observacoes: '',
  checklist: [] as QualidadeChecklistItem[],
};

export default function QualidadePage() {
  const navigate = useNavigate();
  const [inspecoes, setInspecoes] = useState<QualidadeInspecao[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [ordensProducao, setOrdensProducao] = useState<OrdemProducao[]>([]);
  const [responsaveis, setResponsaveis] = useState<Responsavel[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [processando, setProcessando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroResultado, setFiltroResultado] = useState('');

  const [dialog, setDialog] = useState(false);
  const [somenteVisualizar, setSomenteVisualizar] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);

  const [dialogReprovar, setDialogReprovar] = useState(false);
  const [inspecaoReprovar, setInspecaoReprovar] = useState<QualidadeInspecao | null>(null);
  const [acaoCorretivaTexto, setAcaoCorretivaTexto] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await qualidadeInspecaoService.listar({
        tipo: filtroTipo || undefined,
        resultado: filtroResultado || undefined,
      });
      setInspecoes(data);
    } catch (error) {
      console.error('Erro ao carregar inspeções de qualidade', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loteService.listar().then(setLotes).catch(console.error);
    ordemProducaoService.listar().then(setOrdensProducao).catch(console.error);
    responsavelService.listar().then(setResponsaveis).catch(console.error);
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTipo, filtroResultado]);

  const inspecoesFiltradas = useMemo(() => {
    if (!busca) return inspecoes;
    const termo = busca.toLowerCase();
    return inspecoes.filter((i) => (i.lote_numero || '').toLowerCase().includes(termo));
  }, [inspecoes, busca]);

  const abrirDialog = () => {
    setForm(FORM_VAZIO);
    setSomenteVisualizar(false);
    setDialog(true);
  };

  const visualizar = (item: QualidadeInspecao) => {
    setForm({
      tipo: item.tipo,
      lote_id: item.lote_id || '',
      op_id: item.op_id || '',
      data_inspecao: item.data_inspecao ? item.data_inspecao.slice(0, 16) : '',
      resultado: item.resultado,
      responsavel: item.responsavel || '',
      acao_corretiva: item.acao_corretiva || '',
      observacoes: item.observacoes || '',
      checklist: item.checklist || [],
    });
    setSomenteVisualizar(true);
    setDialog(true);
  };

  const adicionarItem = () => {
    setForm({ ...form, checklist: [...form.checklist, { parametro: '', valor_medido: '', limite_min: '', limite_max: '', ok: false, observacao: '' }] });
  };

  const atualizarItem = (index: number, patch: Partial<QualidadeChecklistItem>) => {
    const novos = [...form.checklist];
    novos[index] = { ...novos[index], ...patch };
    setForm({ ...form, checklist: novos });
  };

  const removerItem = (index: number) => {
    setForm({ ...form, checklist: form.checklist.filter((_, i) => i !== index) });
  };

  const salvar = async () => {
    if (!form.tipo || !form.lote_id || !form.data_inspecao || !form.responsavel || !form.resultado) return;
    setSalvando(true);
    try {
      await qualidadeInspecaoService.criar({
        tipo: form.tipo,
        lote_id: form.lote_id,
        op_id: form.op_id || null,
        data_inspecao: form.data_inspecao,
        resultado: form.resultado,
        checklist: form.checklist,
        responsavel: form.responsavel,
        observacoes: form.observacoes || null,
      });
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao salvar inspeção de qualidade', error);
    } finally {
      setSalvando(false);
    }
  };

  const aprovar = async (item: QualidadeInspecao) => {
    setProcessando(true);
    try {
      await qualidadeInspecaoService.aprovar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao aprovar inspeção', error);
    } finally {
      setProcessando(false);
    }
  };

  const abrirReprovar = (item: QualidadeInspecao) => {
    setInspecaoReprovar(item);
    setAcaoCorretivaTexto('');
    setDialogReprovar(true);
  };

  const confirmarReprovar = async () => {
    if (!inspecaoReprovar) return;
    setProcessando(true);
    try {
      await qualidadeInspecaoService.reprovar(inspecaoReprovar.id, acaoCorretivaTexto);
      setDialogReprovar(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao reprovar inspeção', error);
    } finally {
      setProcessando(false);
    }
  };

  const deletar = async (item: QualidadeInspecao) => {
    try {
      await qualidadeInspecaoService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir inspeção', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<ShieldCheck size={21} strokeWidth={2} />}
        title="Inspeções de Qualidade"
        description="Registro e acompanhamento das inspeções de qualidade dos lotes."
        actions={
          <Button type="button" onClick={() => navigate('/producao/qualidade/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova Inspeção
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar lote..."
                />
              </div>
              <Select filter value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="">Todos os tipos</option>
                {Object.entries(TIPO_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <Select filter value={filtroResultado} onChange={(e) => setFiltroResultado(e.target.value)}>
                <option value="">Todos os resultados</option>
                {Object.entries(RESULTADO_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
          }
        />

        {!loading && inspecoesFiltradas.length === 0 ? (
          <EmptyState message="Nenhuma inspeção encontrada." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Lote</th>
                  <th>Data</th>
                  <th>Resultado</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  inspecoesFiltradas.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <StatusBadge label={TIPO_LABELS[item.tipo]} tone={TIPO_TONES[item.tipo]} />
                      </td>
                      <td>{item.lote_numero}</td>
                      <td>{formatDateTime(item.data_inspecao)}</td>
                      <td>
                        <StatusBadge label={RESULTADO_LABELS[item.resultado]} tone={RESULTADO_TONES[item.resultado]} />
                      </td>
                      <td className="nx-col-acoes">
                        <div className="nx-table-actions-group nx-table-actions-group--buttons">
                          <IconButton label="Detalhes" onClick={() => visualizar(item)}>
                            <Eye size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton label="Editar" onClick={() => navigate(`/producao/qualidade/${item.id}/editar`)}>
                            <Pencil size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label="Aprovar"
                            onClick={() => aprovar(item)}
                            disabled={item.resultado === 'APROVADO' || processando}
                          >
                            <Check size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label="Reprovar"
                            variant="danger"
                            onClick={() => abrirReprovar(item)}
                            disabled={item.resultado === 'REPROVADO'}
                          >
                            <X size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <TableActions
                            onDelete={() => deletar(item)}
                            deleteTitle="Excluir inspeção"
                            deleteDescription={`Deseja realmente excluir a inspeção do lote "${item.lote_numero}"? Esta ação não poderá ser desfeita.`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>{somenteVisualizar ? 'Detalhes da Inspeção' : 'Nova Inspeção de Qualidade'}</DialogTitle>
            <DialogDescription>
              {somenteVisualizar
                ? 'Consulte os dados e o checklist desta inspeção de qualidade.'
                : 'Preencha os dados e o checklist da nova inspeção de qualidade.'}
            </DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <fieldset disabled={somenteVisualizar} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
              <div className="nx-form-grid">
                <Field label="Tipo de Inspeção *" htmlFor="qualidade-tipo">
                  <Select
                    id="qualidade-tipo"
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value as QualidadeTipo })}
                  >
                    {Object.entries(TIPO_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Lote *" htmlFor="qualidade-lote">
                  <Select
                    id="qualidade-lote"
                    value={form.lote_id}
                    onChange={(e) => setForm({ ...form, lote_id: e.target.value })}
                  >
                    <option value="">Selecione</option>
                    {lotes.map((l) => (
                      <option key={l.id} value={l.id}>{l.numero_lote} - {l.produto_nome}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Ordem de Produção" htmlFor="qualidade-op">
                  <Select
                    id="qualidade-op"
                    value={form.op_id}
                    onChange={(e) => setForm({ ...form, op_id: e.target.value })}
                  >
                    <option value="">-</option>
                    {ordensProducao.map((o) => (
                      <option key={o.id} value={o.id}>{o.numero} - {o.produto_nome}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Data da Inspeção *" htmlFor="qualidade-data">
                  <Input
                    id="qualidade-data"
                    type="datetime-local"
                    value={form.data_inspecao}
                    onChange={(e) => setForm({ ...form, data_inspecao: e.target.value })}
                  />
                </Field>
                <Field label="Inspetor *" htmlFor="qualidade-responsavel">
                  <Select
                    id="qualidade-responsavel"
                    value={form.responsavel}
                    onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                  >
                    <option value="">Selecione</option>
                    {responsaveis.map((r) => (
                      <option key={r.id} value={r.nome}>{r.nome}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Resultado *" htmlFor="qualidade-resultado">
                  <Select
                    id="qualidade-resultado"
                    value={form.resultado}
                    onChange={(e) => setForm({ ...form, resultado: e.target.value as QualidadeResultado })}
                  >
                    {Object.entries(RESULTADO_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="nx-field-label">Checklist de Inspeção</span>
                  {!somenteVisualizar && (
                    <Button type="button" variant="ghost" onClick={adicionarItem}>
                      <Plus size={14} strokeWidth={2.2} />
                      Adicionar Item
                    </Button>
                  )}
                </div>
                <div className="nx-table-wrap">
                  <table className="nx-table">
                    <thead>
                      <tr>
                        <th>Parâmetro</th>
                        <th>Valor Medido</th>
                        <th>Limite Mín</th>
                        <th>Limite Máx</th>
                        <th>OK?</th>
                        <th>Observação</th>
                        <th className="nx-col-acoes">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.checklist.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="nx-table-loading">Adicione itens ao checklist</td>
                        </tr>
                      ) : (
                        form.checklist.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <Input
                                value={item.parametro}
                                onChange={(e) => atualizarItem(index, { parametro: e.target.value })}
                                placeholder="Ex: Peso"
                              />
                            </td>
                            <td>
                              <Input
                                value={item.valor_medido || ''}
                                onChange={(e) => atualizarItem(index, { valor_medido: e.target.value })}
                              />
                            </td>
                            <td>
                              <Input
                                value={item.limite_min || ''}
                                onChange={(e) => atualizarItem(index, { limite_min: e.target.value })}
                              />
                            </td>
                            <td>
                              <Input
                                value={item.limite_max || ''}
                                onChange={(e) => atualizarItem(index, { limite_max: e.target.value })}
                              />
                            </td>
                            <td>
                              <Checkbox
                                checked={!!item.ok}
                                onChange={(e) => atualizarItem(index, { ok: e.target.checked })}
                              />
                            </td>
                            <td>
                              <Input
                                value={item.observacao || ''}
                                onChange={(e) => atualizarItem(index, { observacao: e.target.value })}
                              />
                            </td>
                            <td className="nx-col-acoes">
                              {!somenteVisualizar && (
                                <IconButton label="Remover" variant="danger" onClick={() => removerItem(index)}>
                                  <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                                </IconButton>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="nx-form-grid" style={{ marginTop: '1.25rem' }}>
                <Field label="Observações Gerais" htmlFor="qualidade-observacoes" className="nx-sngpc-field--full">
                  <Textarea
                    id="qualidade-observacoes"
                    value={form.observacoes}
                    onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                    rows={2}
                  />
                </Field>
              </div>

              {somenteVisualizar && form.acao_corretiva && (
                <p className="nx-form-hint" style={{ marginTop: '0.75rem' }}>
                  <strong>Ação corretiva:</strong> {form.acao_corretiva}
                </p>
              )}
            </fieldset>
          </div>

          <DialogFooter>
            {somenteVisualizar ? (
              <DialogClose asChild>
                <Button type="button" variant="ghost">Fechar</Button>
              </DialogClose>
            ) : (
              <>
                <DialogClose asChild>
                  <Button type="button" variant="ghost" disabled={salvando}>Cancelar</Button>
                </DialogClose>
                <Button type="submit" loading={salvando} loadingText="Salvando...">
                  <Save size={16} /> Salvar
                </Button>
              </>
            )}
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogReprovar} onOpenChange={(next) => !processando && setDialogReprovar(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprovar Inspeção</DialogTitle>
            <DialogDescription>
              O lote associado será bloqueado (status BLOQUEADO).
            </DialogDescription>
          </DialogHeader>
          <Field label="Ação corretiva" htmlFor="reprovar-acao-corretiva">
            <Textarea
              id="reprovar-acao-corretiva"
              value={acaoCorretivaTexto}
              onChange={(e) => setAcaoCorretivaTexto(e.target.value)}
              rows={3}
            />
          </Field>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={processando}>Cancelar</Button>
            </DialogClose>
            <Button type="button" variant="danger" loading={processando} loadingText="Reprovando..." onClick={confirmarReprovar}>
              Confirmar Reprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
